import Navbar from '@app/components/layout/nav/navbar';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { ReportType } from '../types';
import { getReportsPathMap } from '../utils';

interface ReportLayoutHeaderProps {
  reportType: ReportType;
  reportUuid: string;
  handleDownloadCSV?: () => void;
  handleDownloadPDF?: () => void;
  isDownloadCSV?: boolean;
  onBack?: () => void;
  backLabel?: string;
  customActions?: React.ReactNode;
}

export default function ReportLayoutHeader({
  reportType,
  reportUuid,
  handleDownloadCSV,
  handleDownloadPDF,
  isDownloadCSV = false,
  onBack,
  backLabel,
  customActions,
}: ReportLayoutHeaderProps) {
  const navigate = useNavigate();
  const { track } = useAnalytics();

  const trackDownloadPdf = useCallback(
    () =>
      track('Downloaded Report PDF', {
        reportType,
        reportUuid,
      }),
    [reportType, reportUuid, track]
  );

  const handleBack =
    onBack ??
    (() =>
      navigate(
        reportType === 'ccf'
          ? generatePath(getReportsPathMap()[reportType].report, {
              report_uuid: reportUuid,
            })
          : generatePath(getReportsPathMap()[reportType].list)
      ));

  const resolvedBackLabel =
    backLabel ??
    (reportType === 'ccf' ? 'Back to dashboard' : 'Back to reports');

  return (
    <header className="flex h-[--header-height] w-full items-center justify-between border-b bg-primary">
      <Navbar
        logo={{
          variant: 'name',
          color: 'light',
        }}
      />
      <div className="flex items-center space-x-2 p-4">
        <Button
          icon={<Icons.chevronLeft className="size-4" />}
          variant="ghost"
          className="text-primary-50 hover:bg-transparent hover:text-primary-300"
          disabled={isDownloadCSV}
          onClick={handleBack}
        >
          {resolvedBackLabel}
        </Button>
        {customActions}
        {(handleDownloadCSV || handleDownloadPDF) && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center" asChild>
              <Button
                variant="outline"
                disabled={isDownloadCSV}
                loading={isDownloadCSV}
                data-testid="download-report-button"
              >
                Download
                <Icons.chevronDown className="ml-1 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {handleDownloadPDF && (
                <DropdownMenuItem
                  onClick={() => {
                    trackDownloadPdf();
                    handleDownloadPDF();
                  }}
                >
                  <Icons.fileChartColumn className="mr-1 size-4" />
                  Download report as PDF
                </DropdownMenuItem>
              )}
              {handleDownloadCSV && (
                <DropdownMenuItem onClick={handleDownloadCSV}>
                  <Icons.fileSpreadsheet className="mr-1 size-4" />
                  Export data as CSV
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
